import React, { useState, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Paper,
  Divider,
  Chip,
  CircularProgress,
  Autocomplete,
  FormControlLabel,
  Switch
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EventIcon from '@mui/icons-material/Event';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { RootState } from '../store/store';
import { searchCities, fetchCategories } from '../services/apiService';
import { buildEventsApiUrl } from '../config/api';
import { getAuthUserId } from '../utils/authUser';

interface CityOption {
  city: string;
  state: string;
  stateAbbr: string;
  lat: number;
  lng: number;
}

interface CreateEventForm {
  eventName: string;
  description: string;
  category: string;
  eventDateTime: string;
  eventStartDateTime: string;
  mustArriveByDateTime: string;
  tradeCutoffDate: string;
  selectedCity: CityOption | null;
  venueAddress: string;
  maxParticipants: string;
  participantInitiationEnabled: boolean;
  status: 'draft' | 'active';
}



export default function CreateEvent() {
  const navigate = useNavigate();
  const { isAuthenticated, user, initialized } = useSelector((state: RootState) => state.auth);

  const [formData, setFormData] = useState<CreateEventForm>({
    eventName: '',
    description: '',
    category: '',
    eventDateTime: '',
    eventStartDateTime: '',
    mustArriveByDateTime: '',
    tradeCutoffDate: '',
    selectedCity: null,
    venueAddress: '',
    maxParticipants: '',
    participantInitiationEnabled: true,
    status: 'active'
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [citySearchInput, setCitySearchInput] = useState('');
  const [cityOptions, setCityOptions] = useState<CityOption[]>([]);
  const [citySearchLoading, setCitySearchLoading] = useState(false);
  const [citySearchTimeout, setCitySearchTimeout] = React.useState<NodeJS.Timeout | null>(null);
  const [categories, setCategories] = useState<string[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);

  // Redirect if not authenticated
  React.useEffect(() => {
    if (!initialized) {
      return;
    }

    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
  }, [initialized, isAuthenticated, navigate]);

  // Load categories from backend
  React.useEffect(() => {
    const loadCategories = async () => {
      try {
        console.log('📚 Loading categories...');
        const categoryList = await fetchCategories();
        setCategories(categoryList.sort());
      } catch (err: any) {
        console.error('❌ Error:', err);
        setCategories(['Books', 'Collectables', 'Electronics', 'Sports Equipment', 'Tabletop Games', 'Toys', 'Video Games']);
      } finally {
        setCategoriesLoading(false);
      }
    };
    loadCategories();
  }, []);

    const handleCitySearch = useCallback(
    async (query: string) => {
      if (citySearchTimeout) clearTimeout(citySearchTimeout);

      if (!query || query.trim().length < 2) {
        setCityOptions([]);
        return;
      }

      setCitySearchLoading(true);

      const timeout = setTimeout(async () => {
        try {
          console.log('🔍 Searching cities:', query);
          const results = await searchCities(query);
          console.log(`✅ Got ${results.length} results`);
          setCityOptions(results);
        } catch (err: any) {
          console.error('❌ Search error:', err);
          setCityOptions([]);
        } finally {
          setCitySearchLoading(false);
        }
      }, 300);

      setCitySearchTimeout(timeout);
    },
    [citySearchTimeout]
  );

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!formData.eventName.trim()) {
      setError('Event name is required');
      return;
    }
    
    if (!formData.description.trim()) {
      setError('Event description is required');
      return;
    }

    if (!formData.category) {
      setError('Category is required');
      return;
    }

    if (!formData.eventDateTime) {
      setError('Event date and time are required');
      return;
    }

    // Check if event date is in the future
    const eventDate = new Date(formData.eventDateTime);
    if (eventDate <= new Date()) {
      setError('Event date must be in the future');
      return;
    }
    
    if (formData.tradeCutoffDate) {
      const cutoffDate = new Date(formData.tradeCutoffDate);
      if (cutoffDate >= eventDate) {
        setError('Trade cutoff date must be before the event date');
        return;
      }
    }

    if (formData.mustArriveByDateTime) {
      const mustArriveByDate = new Date(formData.mustArriveByDateTime);
      if (mustArriveByDate <= eventDate) {
        setError('Must arrive by date and time must be after the event date and time');
        return;
      }
    }

    if (!formData.selectedCity) {
      setError('Location is required');
      return;
    }

    if (!formData.venueAddress.trim()) {
      setError('Venue address is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const eventData = {
        eventName: formData.eventName,
        description: formData.description,
        category: formData.category,
        eventDateTime: formData.eventDateTime,
        eventStartDateTime: formData.eventDateTime,
        mustArriveByDateTime: formData.mustArriveByDateTime || '',
        tradeCutoffDate: formData.tradeCutoffDate || null,
        listingCityName: formData.selectedCity.city,
        listingStateName: formData.selectedCity.state,
        venueAddress: formData.venueAddress,
        maxParticipants: formData.maxParticipants ? parseInt(formData.maxParticipants) : null,
        participantInitiationEnabled: formData.participantInitiationEnabled,
        ownerID: getAuthUserId(user),
        coordinates: {
          lat: formData.selectedCity.lat,
          lng: formData.selectedCity.lng
        },
        status: formData.status
      };
      
      console.log('🎯 Creating event with data:', eventData);
      
      // Call backend API to create event
      const response = await fetch(buildEventsApiUrl('/api/events'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(eventData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('✅ Event created:', result);
      
      setSuccess(true);
      
      // Redirect to dashboard after success
      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);
    } catch (err: any) {
      setError(err.message || 'Failed to create event');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, gap: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/dashboard')}
          sx={{ textTransform: 'none' }}
        >
          Back to Dashboard
        </Button>
        <Typography variant="h4" sx={{ fontWeight: '700', display: 'flex', alignItems: 'center', gap: 1 }}>
          <EventIcon /> Create New Event
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          {/* Success Message */}
          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              ✅ Event created successfully! Redirecting...
            </Alert>
          )}

          {/* Error Message */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Event Name */}
              <Grid item xs={12}>
                <TextField
                  label="Event Name"
                  fullWidth
                  required
                  value={formData.eventName}
                  onChange={(e) => handleInputChange('eventName', e.target.value)}
                  disabled={loading}
                  placeholder="e.g., Chicago Gaming Trade Meetup"
                />
              </Grid>

              {/* Description */}
              <Grid item xs={12}>
                <TextField
                  label="Event Description"
                  fullWidth
                  required
                  multiline
                  rows={4}
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  disabled={loading}
                  placeholder="Describe your event..."
                />
              </Grid>

              {/* Category & Status */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={formData.category}
                    onChange={(e) => handleInputChange('category', e.target.value)}
                    disabled={loading || categoriesLoading}
                    label="Category"
                  >
                    {categories.map((category) => (
                      <MenuItem key={category} value={category}>
                        {category}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Max Participants (optional)"
                  type="number"
                  fullWidth
                  value={formData.maxParticipants}
                  onChange={(e) => handleInputChange('maxParticipants', e.target.value)}
                  disabled={loading}
                  placeholder="Leave empty for unlimited"
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', mt: 2 }}>
                  📅 Event Schedule
                </Typography>
              </Grid>

              {/* Event Date & Time */}
              <Grid item xs={12} md={6}>
                <TextField
                  label="Event Date & Time"
                  type="datetime-local"
                  fullWidth
                  required
                  value={formData.eventDateTime}
                  onChange={(e) => handleInputChange('eventDateTime', e.target.value)}
                  disabled={loading}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              {/* Trade Cutoff Date */}
              <Grid item xs={12} md={6}>
                <TextField
                  label="Trade Cutoff Date (optional)"
                  type="datetime-local"
                  fullWidth
                  value={formData.tradeCutoffDate}
                  onChange={(e) => handleInputChange('tradeCutoffDate', e.target.value)}
                  disabled={loading}
                  InputLabelProps={{ shrink: true }}
                  helperText="Last time to add/modify items"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Must Arrive By"
                  type="datetime-local"
                  fullWidth
                  value={formData.mustArriveByDateTime}
                  onChange={(e) => handleInputChange('mustArriveByDateTime', e.target.value)}
                  disabled={loading}
                  InputLabelProps={{ shrink: true }}
                  helperText="This must be after the event date and time. Late arrivals can still participate if the event is still active"
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch checked={formData.participantInitiationEnabled} onChange={(e) => handleInputChange('participantInitiationEnabled', e.target.checked)} />}
                  label="Allow participants to initiate eligible group trades"
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', mt: 2 }}>
                  <LocationOnIcon sx={{ mr: 1 }} />
                  Event Location
                </Typography>
              </Grid>

              {/* City Search Autocomplete */}
              <Grid item xs={12}>
                <Autocomplete
                  freeSolo
                  options={cityOptions}
                  getOptionLabel={(option) =>
                    typeof option === 'string'
                      ? option
                      : `${option.city}, ${option.stateAbbr}`
                  }
                  value={formData.selectedCity}
                  onChange={(event, newValue) => {
                    if (newValue && typeof newValue !== 'string') {
                      handleInputChange('selectedCity', newValue);
                    }
                  }}
                  inputValue={citySearchInput}
                  onInputChange={(event, newInputValue) => {
                    setCitySearchInput(newInputValue);
                    handleCitySearch(newInputValue);
                  }}
                  loading={citySearchLoading}
                  disabled={loading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Event Location"
                      placeholder="e.g., 'New York' or 'Appleton, WI'"
                      fullWidth
                      required
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {citySearchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => {
                    const { key, ...otherProps } = props;
                    return (
                      <Box component="li" key={key} {...otherProps}>
                        <Box>
                          <Typography variant="body2">
                            {option.city}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#999' }}>
                            {option.state} ({option.stateAbbr})
                          </Typography>
                        </Box>
                      </Box>
                    );
                  }}
                  noOptionsText={citySearchInput.length < 2 ? 'Type at least 2 characters' : 'No cities found'}
                  ListboxProps={{
                    sx: { maxHeight: 300 }
                  }}
                />
              </Grid>

              {/* Venue Address */}
              <Grid item xs={12}>
                <TextField
                  label="Venue Address"
                  fullWidth
                  required
                  value={formData.venueAddress}
                  onChange={(e) => handleInputChange('venueAddress', e.target.value)}
                  disabled={loading}
                  placeholder="123 Main St, Community Center, etc."
                />
              </Grid>

              {/* Selected Location Preview */}
              {formData.selectedCity && (
                <Grid item xs={12}>
                  <Paper elevation={1} sx={{ p: 2, backgroundColor: '#f0f4ff', borderRadius: '8px' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      ✅ Selected Location:
                    </Typography>
                    <Typography variant="body2">
                      {formData.selectedCity.city}, {formData.selectedCity.state}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#666', display: 'block', mt: 1 }}>
                      📍 {formData.selectedCity.lat.toFixed(4)}, {formData.selectedCity.lng.toFixed(4)}
                    </Typography>
                  </Paper>
                </Grid>
              )}

              {/* Event Preview */}
              {formData.eventName && formData.category && (
                <Grid item xs={12}>
                  <Paper elevation={1} sx={{ p: 3, backgroundColor: '#f8f9fa' }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      📋 Event Preview
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                      <Chip 
                        label={formData.category} 
                        sx={{ backgroundColor: '#667eea', color: 'white' }} 
                      />
                      {formData.selectedCity && (
                        <Chip 
                          label={`${formData.selectedCity.city}, ${formData.selectedCity.state}`} 
                          icon={<LocationOnIcon />}
                          variant="outlined"
                        />
                      )}
                    </Box>
                    <Typography variant="body2">{formData.description}</Typography>
                  </Paper>
                </Grid>
              )}

              {/* Submit Button */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button
                    onClick={() => navigate('/dashboard')}
                    disabled={loading}
                    sx={{ textTransform: 'none' }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={loading || !formData.selectedCity}
                    sx={{
                      background: 'linear-gradient(45deg, #667eea, #764ba2)',
                      textTransform: 'none',
                      fontSize: '1rem',
                      fontWeight: 600,
                      px: 4
                    }}
                  >
                    {loading ? (
                      <>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        Creating...
                      </>
                    ) : (
                      'Create Event'
                    )}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </Grid>

        {/* Right Sidebar - Info */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: '600' }}>
                ℹ️ Event Tips
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: '600', mb: 1 }}>
                    📍 Location
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    Start typing a city or state name. Results appear instantly.
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: '600', mb: 1 }}>
                    📅 Dates
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    Event date must be in the future. Trade cutoff should be before event date.
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: '600', mb: 1 }}>
                    👥 Participants
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    Leave max participants empty for unlimited. Set a number to cap attendance.
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
}












